import React, { useState } from 'react';
import { ExerciseProgram, ProgramDay, Exercise, DailyRecord } from '../types';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { getLocalizedDifficulty } from '../lib/exerciseTranslations';
import { apiPost, apiPut, apiDelete } from '../lib/api';
import { 
  Plus, Trash2, Edit2, CheckCircle2, Sparkles, Folder, FileCode, Dumbbell, 
  Layers, ChevronRight, Activity, ShieldAlert 
} from 'lucide-react';
import ProgramDayManager from './ProgramDayManager';
import ProgramJsonImportModal from './ProgramJsonImportModal';

interface ProgramListManagerProps {
  programs: ExerciseProgram[];
  activeProgramId?: string;
  availableExercises: Exercise[];
  onProgramsChanged: (updatedPrograms: ExerciseProgram[]) => void;
  onSelectActiveProgram: (programId: string) => void;
  onStartGuidedWorkout: (day: ProgramDay, program: ExerciseProgram) => void;
  onScheduleDay: (day: ProgramDay, program: ExerciseProgram) => void;
  record?: DailyRecord;
  onUpdateRecord?: (updatedRecord: DailyRecord) => void;
}

export default function ProgramListManager({
  programs,
  activeProgramId,
  availableExercises,
  onProgramsChanged,
  onSelectActiveProgram,
  onStartGuidedWorkout,
  onScheduleDay,
  record,
  onUpdateRecord
}: ProgramListManagerProps) {
  const { t, lang } = useLanguage();

  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(
    activeProgramId || (programs?.[0]?.id || null)
  );

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Program Creation / Edit Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingProg, setEditingProg] = useState<ExerciseProgram | null>(null);

  const [progTitle, setProgTitle] = useState('');
  const [progDesc, setProgDesc] = useState('');
  const [progCategory, setProgCategory] = useState('rehab');
  const [progDifficulty, setProgDifficulty] = useState<'Beginner' | 'Intermediate' | 'Advanced'>('Beginner');
  const [loading, setLoading] = useState(false);

  const selectedProgram = programs.find(p => p.id === selectedProgramId) || programs[0] || null;

  const handleOpenCreateModal = (progToEdit?: ExerciseProgram) => {
    if (progToEdit) {
      setEditingProg(progToEdit);
      setProgTitle(progToEdit.title);
      setProgDesc(progToEdit.description || '');
      setProgCategory(progToEdit.category || 'rehab');
      setProgDifficulty(progToEdit.difficulty || 'Beginner');
    } else {
      setEditingProg(null);
      setProgTitle('');
      setProgDesc('');
      setProgCategory('rehab');
      setProgDifficulty('Beginner');
    }
    setIsCreateModalOpen(true);
  };

  const handleSaveProgram = async () => {
    if (!progTitle.trim()) return;

    try {
      setLoading(true);

      if (editingProg) {
        // Update
        const res = await apiPut(`/api/rehab/programs/${editingProg.id}`, {
          title: progTitle.trim(),
          description: progDesc.trim(),
          category: progCategory,
          difficulty: progDifficulty
        });

        if (res && res.success) {
          const updated = programs.map(p => {
            if (p.id === editingProg.id) {
              return {
                ...p,
                title: progTitle.trim(),
                description: progDesc.trim(),
                category: progCategory,
                difficulty: progDifficulty
              };
            }
            return p;
          });
          onProgramsChanged(updated);
          setIsCreateModalOpen(false);
        }
      } else {
        // Create
        const res = await apiPost('/api/rehab/programs', {
          title: progTitle.trim(),
          description: progDesc.trim(),
          category: progCategory,
          difficulty: progDifficulty,
          days: []
        });

        if (res && res.success && res.data) {
          const updated = [res.data, ...programs];
          onProgramsChanged(updated);
          setSelectedProgramId(res.data.id);
          setIsCreateModalOpen(false);
        }
      }
    } catch (err: any) {
      alert(err?.message || t('dbProgramSaveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProgram = async (progId: string) => {
    if (confirm(t('confirmDeleteProgram'))) {
      try {
        await apiDelete(`/api/rehab/programs/${progId}`);
        const filtered = programs.filter(p => p.id !== progId);
        onProgramsChanged(filtered);
        if (selectedProgramId === progId) {
          setSelectedProgramId(filtered[0]?.id || null);
        }
      } catch (e: any) {
        alert(e?.message || t('dbProgramDeleteError'));
      }
    }
  };

  const handleActivateProgram = async (progId: string) => {
    try {
      await apiPost(`/api/rehab/programs/${progId}/activate`, {});
      const updated = programs.map(p => ({
        ...p,
        isActive: p.id === progId
      }));
      onProgramsChanged(updated);
      onSelectActiveProgram(progId);
    } catch (e: any) {
      alert(e?.message || t('dbProgramActivateError'));
    }
  };

  const handleUpdateProgramDays = async (updatedProg: ExerciseProgram) => {
    try {
      await apiPut(`/api/rehab/programs/${updatedProg.id}`, {
        title: updatedProg.title,
        description: updatedProg.description,
        days: updatedProg.days
      });

      const updatedList = programs.map(p => p.id === updatedProg.id ? updatedProg : p);
      onProgramsChanged(updatedList);
    } catch (e: any) {
      alert(e?.message || t('dbProgramUpdateDaysError'));
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Top Banner / Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white rounded-3xl shadow-xl border border-purple-500/30">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Layers className="w-6 h-6 text-purple-400" />
            <h2 className="text-lg font-bold">
              {t('multiDayProgramsHeader', 'مدیریت برنامه‌های تمرینی چندروزه فیزیوتراپی')}
            </h2>
          </div>
          <p className="text-xs text-purple-200/80">
            {t('multiDayProgramsDesc', 'طراحی برنامه‌های چندروزه، تعریف حرکات مجزا برای هر روز، درون‌ریزی JSON و همگام‌سازی با روتین هفتگی')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="px-4 py-2 bg-purple-700/80 hover:bg-purple-600 text-white text-xs font-bold rounded-xl border border-purple-400/40 shadow-sm flex items-center gap-2 transition-all cursor-pointer"
          >
            <FileCode className="w-4 h-4 text-purple-300" />
            <span>{t('importJsonBtn', 'درون‌ریزی JSON برنامه')}</span>
          </button>

          <button
            onClick={() => handleOpenCreateModal()}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-xs font-bold rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>{t('createNewProgramBtn', 'ایجاد برنامه تمرینی جدید')}</span>
          </button>
        </div>
      </div>

      {/* Main Content Grid: Left/Top Cards Selector, Right/Bottom Day Manager */}
      {programs.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-gray-800/80 rounded-3xl border border-dashed border-gray-300 dark:border-gray-700 shadow-xs space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-purple-100 dark:bg-purple-950/60 text-purple-600 dark:text-purple-300 flex items-center justify-center mx-auto shadow-inner">
            <Dumbbell className="w-8 h-8" />
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            {t('noProgramsYetTitle', 'هنوز برنامه تمرینی چندروزه‌ای تعریف نشده است')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {t('noProgramsYetDesc', 'شما می‌توانید یک برنامه چندروزه جدید ایجاد کنید یا از الگوهای تخصصی آماده فیزیوتراپی (دستورات JSON) استفاده نمایید.')}
          </p>
          <div className="flex justify-center gap-3 pt-2">
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="px-4 py-2 bg-purple-600 text-white text-xs font-bold rounded-xl cursor-pointer hover:bg-purple-700 transition-colors inline-flex items-center gap-2 shadow-xs"
            >
              <FileCode className="w-4 h-4" />
              <span>{t('loadSampleProgramJson', 'بارگذاری برنامه آماده (نمونه JSON)')}</span>
            </button>
            <button
              onClick={() => handleOpenCreateModal()}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs font-bold rounded-xl cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>{t('buildManualProgram', 'ساخت برنامه دستی')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* Program Selector Cards Carousel / Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((prog) => {
              const isSelected = selectedProgramId === prog.id;
              const isActive = prog.isActive || activeProgramId === prog.id;

              return (
                <div
                  key={prog.id}
                  onClick={() => setSelectedProgramId(prog.id)}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer relative overflow-hidden flex flex-col justify-between ${
                    isSelected
                      ? 'bg-white dark:bg-gray-800 border-purple-500 dark:border-purple-400 shadow-lg ring-2 ring-purple-500/20'
                      : 'bg-white/80 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-800 shadow-xs'
                  }`}
                >
                  {/* Active Badge indicator bar */}
                  {isActive && (
                    <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
                  )}

                  <div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-bold text-sm text-gray-900 dark:text-white leading-snug">
                        {prog.title}
                      </h4>
                      {isActive && (
                        <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold rounded-full border border-emerald-300 dark:border-emerald-800 shrink-0">
                          {t('activeProgramBadge', 'برنامه فعال')}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                      {prog.description || t('rehabNoDescription', 'بدون توضیحات تکمیلی')}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300 mb-4">
                      <span className="px-2.5 py-0.5 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-bold rounded-lg border border-purple-200 dark:border-purple-800 font-mono">
                        {t('rehabDaysCount', undefined, { count: formatNumberByLang(prog.days?.length || 0, lang) })}
                      </span>
                      <span className="px-2.5 py-0.5 bg-gray-100 dark:bg-gray-700 font-medium rounded-lg">
                        {getLocalizedDifficulty(prog.difficulty, lang)}
                      </span>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700/60 text-xs">
                    {!isActive ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActivateProgram(prog.id);
                        }}
                        className="text-purple-600 dark:text-purple-400 font-bold hover:underline cursor-pointer"
                      >
                        {t('setAsActiveProgram', 'فعال‌سازی این برنامه')}
                      </button>
                    ) : (
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>{t('rehabActiveProgramCurrent', 'برنامه فعال جاری')}</span>
                      </span>
                    )}

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCreateModal(prog);
                        }}
                        className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 rounded-lg cursor-pointer"
                        title={t('rehabEditTitle', 'ویرایش عنوان و ویژگی‌ها')}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProgram(prog.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg cursor-pointer"
                        title={t('rehabDeleteTitle', 'حذف برنامه')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* Active / Selected Program Days Manager */}
          {selectedProgram && (
            <div className="p-6 bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
              <ProgramDayManager
                program={selectedProgram}
                availableExercises={availableExercises}
                onUpdateProgram={handleUpdateProgramDays}
                onStartGuidedWorkout={(day) => onStartGuidedWorkout(day, selectedProgram)}
                onScheduleDay={(day) => onScheduleDay(day, selectedProgram)}
                record={record}
                onUpdateRecord={onUpdateRecord}
              />
            </div>
          )}

        </div>
      )}

      {/* Program Create / Edit Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-purple-200 dark:border-purple-900/40 w-full max-w-md overflow-hidden flex flex-col">
            
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                {editingProg ? t('rehabEditProgram', 'ویرایش اطلاعات برنامه تمرینی') : t('rehabCreateProgram', 'ایجاد برنامه تمرینی جدید')}
              </h3>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg cursor-pointer"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('rehabProgramTitleLabel', 'عنوان برنامه تمرینی:')}
                </label>
                <input
                  type="text"
                  value={progTitle}
                  onChange={(e) => setProgTitle(e.target.value)}
                  placeholder={t('rehabProgramTitlePlaceholder', 'مثلاً: برنامه ۲۱ روزه درمان دیسک کمر')}
                  className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('rehabProgramDescLabel', 'شرح و اهداف درمان:')}
                </label>
                <textarea
                  rows={3}
                  value={progDesc}
                  onChange={(e) => setProgDesc(e.target.value)}
                  placeholder={t('rehabProgramDescPlaceholder', 'مثلاً: پروتکل ۳ مرحله‌ای برای آزادسازی عصب سیاتیک...')}
                  className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('rehabDifficultyLabel', 'سطح دشواری:')}
                </label>
                <select
                  value={progDifficulty}
                  onChange={(e) => setProgDifficulty(e.target.value as any)}
                  className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                >
                  <option value="Beginner">{t('rehabDiffBeginner', 'مبتدی / سبک')}</option>
                  <option value="Intermediate">{t('rehabDiffIntermediate', 'متوسط / استاندارد')}</option>
                  <option value="Advanced">{t('rehabDiffAdvanced', 'پیشرفته / تخصصی')}</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl cursor-pointer"
              >
                {t('cancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={handleSaveProgram}
                disabled={loading || !progTitle.trim()}
                className="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-md cursor-pointer disabled:opacity-50"
              >
                {loading ? t('saving', 'در حال ثبت...') : t('rehabSaveBtn', 'ذخیره برنامه')}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* JSON Bulk Import Modal */}
      <ProgramJsonImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportSuccess={(imported) => {
          onProgramsChanged([...imported, ...programs]);
          if (imported[0]?.id) {
            setSelectedProgramId(imported[0].id);
          }
        }}
      />

    </div>
  );
}
